import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";

const CREATE_PAY_URL =
  window.__PAYGATE_FN__ ||
  "https://europe-west1-ghana-67644.cloudfunctions.net/createPaygatePayment";

const STATUS_LABEL = {
  pending_whatsapp: { text: "En attente (WhatsApp)", className: "text-bg-secondary" },
  awaiting_payment: { text: "En attente de paiement", className: "text-bg-warning" },
  pending: { text: "En attente (livraison)", className: "text-bg-secondary" },
  paid: { text: "Payée", className: "text-bg-success" },
  shipped: { text: "Expédiée", className: "text-bg-info" },
  cancelled: { text: "Annulée", className: "text-bg-danger" },
};

export default function OrderResult() {
  const [params] = useSearchParams();
  const orderId = params.get("orderId") || params.get("order_id") || "";
  const hintedStatus = (params.get("status") || "").toLowerCase(); // parfois renvoyé par PayGate
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!!orderId);
  const [err, setErr] = useState("");
  const [restarting, setRestarting] = useState(false);

  const fmt = useMemo(
    () => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" }),
    []
  );

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setErr("Identifiant de commande manquant.");
      return;
    }

    // écoute temps réel du doc commande
    const ref = doc(db, "ordersWine", orderId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setErr("Commande introuvable.");
        } else {
          setOrder({ id: snap.id, ...snap.data() });
          setErr("");
        }
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr("Impossible de récupérer la commande.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [orderId]);

  const restartPayment = useCallback(async () => {
    if (!orderId) return;
    try {
      setRestarting(true);
      // on récupère le doc pour avoir total + client
      const snap = await getDoc(doc(db, "ordersWine", orderId));
      if (!snap.exists()) throw new Error("Commande introuvable.");
      const data = snap.data();
      const amount = Number(data.total) || 0;
      const customer = {
        name: data.customer?.name || "",
        phone: data.customer?.phone || "",
      };

      const resp = await fetch(CREATE_PAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, amount, customer }),
      });
      if (!resp.ok) throw new Error("Échec de création du paiement.");
      const { paymentUrl } = await resp.json();
      if (!paymentUrl) throw new Error("Lien de paiement introuvable.");

      window.location.href = paymentUrl;
    } catch (e) {
      console.error(e);
      setErr(e.message || "Impossible de redémarrer le paiement.");
    } finally {
      setRestarting(false);
    }
  }, [orderId]);

  const badge =
    STATUS_LABEL[order?.status] || { text: order?.status || "—", className: "text-bg-light" };

  return (
    <div className="container py-4">
      <h2>Résultat du paiement</h2>

      {loading ? (
        <p>Chargement…</p>
      ) : err ? (
        <div className="alert alert-danger">{err}</div>
      ) : (
        <>
          <div className="card mb-3">
            <div className="card-body d-flex flex-wrap justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Commande</div>
                <h5 className="m-0">#{orderId}</h5>
              </div>
              <div className="text-end">
                <div className="small text-muted">Statut</div>
                <span className={`badge ${badge.className}`}>{badge.text}</span>
                {hintedStatus && (
                  <div className="small text-muted mt-1">
                    (retour PSP : {hintedStatus})
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">Résumé</h5>
                  <ul className="list-group mb-3">
                    {(order.items || []).map((it, idx) => (
                      <li key={`${it.id}-${idx}`} className="list-group-item d-flex justify-content-between">
                        <div>
                          <strong>{it.name}</strong>
                          <div className="small text-muted">
                            {it.qty} × {fmt.format(Number(it.price) || 0)}
                          </div>
                        </div>
                        <div className="fw-semibold">
                          {fmt.format((Number(it.price) || 0) * (it.qty || 0))}
                        </div>
                      </li>
                    ))}
                    <li className="list-group-item d-flex justify-content-between">
                      <strong>Total</strong>
                      <strong>{fmt.format(Number(order.total) || 0)}</strong>
                    </li>
                  </ul>

                  <div className="small text-muted">
                    Méthode :{" "}
                    <span className="badge text-bg-light">
                      {order.paymentMethod === "paygate"
                        ? "Mobile (PayGate)"
                        : order.paymentMethod === "whatsapp"
                        ? "WhatsApp"
                        : order.paymentMethod === "cod"
                        ? "À la livraison"
                        : order.paymentMethod || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="card h-100">
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title">Action</h5>

                  {order.status === "awaiting_payment" ? (
                    <>
                      <p className="text-muted">
                        Votre paiement n’est pas encore confirmé. Vous pouvez
                        relancer le paiement mobile.
                      </p>
                      <button
                        className="btn btn-warning mb-2"
                        onClick={restartPayment}
                        disabled={restarting}
                      >
                        {restarting ? "Redirection…" : "Payer maintenant (PayGate)"}
                      </button>
                      <Link to="/orders" className="btn btn-outline-secondary">
                        Voir mes commandes
                      </Link>
                    </>
                  ) : order.status === "paid" ? (
                    <>
                      <div className="alert alert-success">
                        Paiement confirmé ✅. Merci pour votre commande !
                      </div>
                      <Link to="/orders" className="btn btn-primary">
                        Voir mes commandes
                      </Link>
                    </>
                  ) : order.status === "cancelled" ? (
                    <>
                      <div className="alert alert-danger">
                        Paiement annulé / échoué.
                      </div>
                      <button
                        className="btn btn-warning mb-2"
                        onClick={restartPayment}
                        disabled={restarting}
                      >
                        {restarting ? "Redirection…" : "Réessayer le paiement"}
                      </button>
                      <Link to="/" className="btn btn-outline-secondary">
                        Retour à l’accueil
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-muted">
                        Statut actuel : <strong>{badge.text}</strong>.
                      </p>
                      <Link to="/orders" className="btn btn-outline-secondary">
                        Voir mes commandes
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
